import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Printer, Lock, RotateCcw, Check, Loader2, ChevronRight, Send } from 'lucide-react';
import DailyCheckIn from '../components/fastBrain/DailyCheckIn';
import { useAuth } from '../context/AuthContext';
import { compass, brainStates, sheets, sheetsFor, stateById } from '../data/fastBrain';
import SheetFields from '../components/fastBrain/SheetFields';
import SheetLayout from '../components/fastBrain/SheetLayout';
import { usePrintSheets } from '../components/fastBrain/usePrintSheets';
import useFastBrainAnswers from '../components/fastBrain/useFastBrainAnswers';

const POS = { full: [50, 17], stuck: [82, 42], scattered: [70, 80], lost: [30, 80], sparking: [18, 42] };
const allImages = [compass.image, ...sheets.map((s) => s.image)];
const hasContent = (v) => (Array.isArray(v) ? v.some(hasContent) : v && typeof v === 'object' ? Object.values(v).some(hasContent) : typeof v === 'number' || !!v);

const PrintBtn = ({ onClick, label, testId, solid }) => (
  <button onClick={onClick} className={`inline-flex items-center gap-2 font-body text-sm px-4 py-2 rounded-full transition-colors ${solid ? 'bg-[#6b3a5e] text-white hover:bg-[#57304c]' : 'bg-white border border-[#d9cbd6] text-[#6b3a5e] hover:border-[#6b3a5e]'}`} data-testid={testId}>
    <Printer size={15} /> {label}
  </button>
);

const Compass = ({ onPick }) => (
  <div className="relative w-[calc(100%+1.5rem)] -mx-3 sm:mx-auto sm:w-full max-w-lg aspect-square" data-testid="fb-compass">
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" aria-hidden>
      {brainStates.map((s) => <line key={s.id} x1="50" y1="50" x2={POS[s.id][0]} y2={POS[s.id][1]} stroke={s.color} strokeOpacity="0.35" strokeWidth="2.2" strokeLinecap="round" />)}
    </svg>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[30%] aspect-square rounded-full bg-[#f8f4ef] border-[3px] border-[#6b3a5e] flex flex-col items-center justify-center">
      <span className="font-heading text-xl sm:text-2xl text-[#6b3a5e] leading-none">START</span>
      <span className="font-body text-sm sm:text-base text-[#6b3a5e]">here</span>
    </div>
    {brainStates.map((s) => {
      const count = sheetsFor(s.id).length;
      return (
        <button
          key={s.id}
          onClick={() => onPick(s.id)}
          style={{ left: `${POS[s.id][0]}%`, top: `${POS[s.id][1]}%`, background: s.bg }}
          className="absolute -translate-x-1/2 -translate-y-1/2 w-[30%] sm:w-[28%] aspect-square rounded-full flex flex-col items-center justify-center text-center p-1.5 sm:p-2 shadow-sm hover:shadow-lg hover:scale-105 transition-transform"
          data-testid={`fb-state-${s.id}`}
        >
          <span className="font-heading text-base sm:text-xl font-semibold uppercase" style={{ color: s.color }}>{s.name}</span>
          <span className="font-body text-[11px] sm:text-sm text-stone-600 leading-tight">{s.hint}</span>
          <span className="font-body text-xs text-stone-600 mt-1">{count ? `${count} sheet${count > 1 ? 's' : ''}` : 'coming soon'}</span>
        </button>
      );
    })}
  </div>
);

const StateView = ({ state, onOpen, answers }) => {
  const list = sheetsFor(state.id);
  return (
    <div data-testid={`fb-state-view-${state.id}`}>
      <p className="font-body text-xs uppercase tracking-[0.3em] text-stone-500 mb-1">Brain state</p>
      <h2 className="font-heading text-4xl mb-1" style={{ color: state.color }}>{state.name}</h2>
      <p className="font-body text-stone-600 mb-8">{state.hint}{/[?.!]$/.test(state.hint) ? '' : '.'} Pick the sheet that feels right.</p>
      {list.length === 0 ? (
        <p className="font-body text-stone-500 italic" data-testid="fb-state-empty">Worksheets for this brain state are coming soon.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((s) => (
            <button key={s.id} onClick={() => onOpen(s.id)} className="group text-left bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`fb-sheet-card-${s.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-stone-50"><img src={s.image} alt="" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform" /></div>
              <div className="p-4 flex items-start gap-2">
                <div className="flex-1">
                  <p className="font-heading text-xl text-forest">{s.title}</p>
                  <p className="font-body text-xs uppercase tracking-wider text-stone-500">{s.tagline}</p>
                </div>
                {hasContent(answers[s.id]) && <span className="font-body text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">started</span>}
                <ChevronRight size={18} className="text-stone-300 mt-1" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SaveStatus = ({ status }) => (
  <span className="inline-flex items-center gap-1 font-body text-xs text-stone-500" data-testid="fb-save-status">
    {status === 'saving' && <><Loader2 size={12} className="animate-spin" /> Saving…</>}
    {status === 'saved' && <><Check size={12} className="text-emerald-600" /> Saved</>}
    {status === 'error' && <span className="text-red-600">Couldn’t save — check your connection</span>}
  </span>
);

const printAnswers = () => {
  document.body.classList.add('fb-print-answers');
  const done = () => document.body.classList.remove('fb-print-answers');
  window.addEventListener('afterprint', done, { once: true });
  setTimeout(() => window.print(), 50);
};

const ShareBtn = ({ sheet, data, userId }) => {
  const [state, setState] = useState('idle');
  const [sentData, setSentData] = useState(null);
  useEffect(() => { if (state === 'sent' && data !== sentData) setState('again'); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const share = async () => {
    setState('sending');
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fast-brain/share`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: userId, sheet_id: sheet.id, data: data || {} }),
    }).catch(() => null);
    setState(res?.ok ? 'sent' : 'error');
    if (res?.ok) setSentData(data);
  };
  return (
    <button onClick={share} disabled={state === 'sending'} className={`inline-flex items-center gap-2 font-body text-sm px-4 py-2 rounded-full transition-colors ${state === 'sent' ? 'bg-emerald-600 text-white' : 'bg-forest text-cream hover:bg-forest-dark'}`} data-testid={`fb-share-${sheet.id}`}>
      {state === 'sending' ? <Loader2 size={15} className="animate-spin" /> : state === 'sent' ? <Check size={15} /> : <Send size={15} />}
      {state === 'sent' ? 'Sent to Jill' : state === 'error' ? 'Couldn’t send — try again' : state === 'again' ? 'Share again (new changes)' : 'Share with Jill'}
    </button>
  );
};

const SheetView = ({ sheet, data, onChange, status, onPrint, userId }) => {
  const state = stateById(sheet.state);
  return (
    <div id="fb-answers-print" className={sheet.layout ? 'max-w-5xl' : 'max-w-3xl'} data-testid={`fb-sheet-${sheet.id}`}>
      <p className="font-body text-xs uppercase tracking-[0.3em] text-stone-500">Brain state: <span className="font-semibold" style={{ color: state.color }}>{state.name}</span></p>
      <h2 className="font-heading text-4xl md:text-5xl text-forest mt-1">{sheet.title}</h2>
      <p className="font-body text-xs uppercase tracking-[0.25em] text-stone-500 mt-2 mb-6">{sheet.tagline}</p>
      <div className="fb-no-print flex flex-wrap items-center gap-3 mb-8">
        <ShareBtn sheet={sheet} data={data} userId={userId} />
        <PrintBtn onClick={printAnswers} label="Print my answers" testId={`fb-print-answers-${sheet.id}`} solid />
        <PrintBtn onClick={onPrint} label="Print blank sheet" testId={`fb-print-${sheet.id}`} />
        <button onClick={() => window.confirm('Clear everything you wrote on this sheet?') && onChange({})} className="inline-flex items-center gap-1.5 font-body text-sm text-stone-500 hover:text-red-600 px-2" data-testid={`fb-clear-${sheet.id}`}>
          <RotateCcw size={14} /> Start fresh
        </button>
        <SaveStatus status={status} />
      </div>
      <div className="grid md:grid-cols-2 gap-6 rounded-2xl p-6 mb-10" style={{ background: state.bg }}>
        <div>
          <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#6b3a5e] mb-2">When to use this</p>
          <p className="font-body text-stone-700 leading-relaxed">{sheet.when}</p>
        </div>
        <div>
          <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#6b3a5e] mb-2">How it works</p>
          <ol className="space-y-2">
            {sheet.how.map((h, i) => (
              <li key={h} className="flex gap-3 font-body text-stone-700"><span className="w-6 h-6 flex-shrink-0 rounded-full bg-[#6b3a5e] text-white text-xs flex items-center justify-center">{i + 1}</span>{h}</li>
            ))}
          </ol>
        </div>
      </div>
      {sheet.layout ? <SheetLayout sheet={sheet} data={data || {}} onChange={onChange} /> : <SheetFields sheet={sheet} data={data || {}} onChange={onChange} />}
    </div>
  );
};

export default function FastBrainPage() {
  const { user, hasAccess, loading } = useAuth();
  const allowed = user && hasAccess('fast-brain');
  const { answers, update, status } = useFastBrainAnswers(allowed ? user.id : null);
  const { print, portal } = usePrintSheets();
  const [stateId, setStateId] = useState(null);
  const [sheetId, setSheetId] = useState(null);
  const sheet = sheets.find((s) => s.id === sheetId);
  const go = (s, sh = null) => { setStateId(s); setSheetId(sh); window.scrollTo(0, 0); };

  if (loading) return <div className="min-h-screen flex items-center justify-center font-body text-stone-500">Loading…</div>;

  if (!allowed) {
    return (
      <div className="min-h-screen bg-[#faf7f2] py-16 px-4" data-testid="fb-access-gate">
        <div className="max-w-xl mx-auto text-center">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8"><ArrowLeft size={16} /> Back to Programs</Link>
          <div className="bg-white border border-stone-200 rounded-2xl p-10">
            <p className="inline-flex items-center gap-1.5 text-xs font-body text-[#6b3a5e] uppercase tracking-[0.25em] mb-3"><Lock size={12} /> Members Only</p>
            <h1 className="font-heading text-4xl text-forest mb-3">The Spicy Brain Survival Guide</h1>
            <p className="font-body text-stone-600">{!user ? 'Please log in from the Programs page to begin.' : "Your account doesn't have access yet — please reach out to Jill."}</p>
          </div>
        </div>
      </div>
    );
  }

  const back = sheet ? () => go(stateId) : stateId ? () => go(null) : null;
  return (
    <div className="min-h-screen bg-[#faf7f2] py-10 px-4" data-testid="fast-brain-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-10">
          {back ? (
            <button onClick={back} className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm" data-testid="fb-back"><ArrowLeft size={16} /> {sheet ? `Back to ${stateById(stateId).name}` : 'Back to the Compass'}</button>
          ) : (
            <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm" data-testid="fb-back-programs"><ArrowLeft size={16} /> Back to Programs</Link>
          )}
          <PrintBtn onClick={() => print(allImages)} label="Print all blank sheets" testId="fb-print-all" solid />
        </div>

        {sheet ? (
          <SheetView sheet={sheet} data={answers[sheet.id]} onChange={(d) => update(sheet.id, d)} status={status.sheetId === sheet.id ? status.state : 'idle'} onPrint={() => print([sheet.image])} userId={user.id} />
        ) : stateId ? (
          <StateView state={stateById(stateId)} onOpen={(id) => go(stateId, id)} answers={answers} />
        ) : (
          <div className="text-center">
            <p className="font-heading text-2xl sm:text-3xl text-[#6b3a5e] mb-2" data-testid="fb-program-title">The Spicy Brain Survival Guide</p>
            <p className="font-body text-stone-600 max-w-2xl mx-auto mb-12" data-testid="fb-program-intro">Are you a super thinker, a fast brain, neuro-spicy — or just looking for more direction? Plan your way, without the drama, the fight, the lost momentum or squeezing yourself into how other people do it.</p>
            <p className="font-body text-xs uppercase tracking-[0.35em] text-[#6b3a5e] mb-2">{compass.eyebrow}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest mb-3">{compass.title}</h1>
            <p className="font-body text-stone-600 mb-8">{compass.subtitle}</p>
            <Compass onPick={(id) => go(id)} />
            <p className="font-body text-xs uppercase tracking-[0.25em] text-stone-500 mt-8">Pick the brain state that feels most like you today</p>
            <div className="mt-6"><PrintBtn onClick={() => print([compass.image])} label="Print the Compass" testId="fb-print-compass" /></div>
            <DailyCheckIn userId={user.id} onOpenSheet={go} />
          </div>
        )}
      </div>
      {portal}
    </div>
  );
}
